import { archerConstants, IconButton, theme } from '@archer-technologies/ui-components';
import {
  CustomPaging,
  CustomTreeData,
  DataTypeProvider,
  FilteringState,
  GroupingState,
  IntegratedFiltering,
  IntegratedGrouping,
  IntegratedPaging,
  IntegratedSorting,
  PagingState,
  SelectionState,
  SortingState,
  TreeDataState
} from '@devexpress/dx-react-grid';
import {
  ColumnChooser,
  DragDropProvider,
  Grid,
  GroupingPanel,
  PagingPanel,
  Table,
  TableColumnResizing,
  TableColumnVisibility,
  TableGroupRow,
  TableHeaderRow,
  TableSelection,
  TableTreeColumn,
  Toolbar,
  VirtualTable
} from '@devexpress/dx-react-grid-material-ui';
import makeStyles from '@mui/styles/makeStyles';
import * as React from 'react';
import { ActionTypeProvider, CheckTypeProvider, DateTypeProvider, NumberTypeProvider, StatusTypeProvider } from './Columns';
import { GridProps, StyledRootProps } from './Grid.types';
import {
  ActionsPlugin,
  ColumnChooserItem,
  ColumnChooserOverlay,
  GroupingPanelEmptyMessageComponent,
  GroupingPanelItemComponent,
  ImagePlugin,
  PagingPanelContainer,
  TableCellComponent,
  TableComponent,
  TableGroupRowCellComponent,
  TableHeaderRowCellComponent,
  TableHeaderRowFilterPlugin,
  TableHeaderRowGroupButtonComponent,
  TableHeaderRowSortLabel,
  TableHeaderRowTitleComponent,
  TableRowSelectCellComponent,
  ToggleButtonComponent,
  ToolbarAddNew,
  ToolbarClearFilters,
  ToolbarDelete,
  ToolbarEmail,
  ToolbarMore,
  treeExpandIcon
} from './plugins';
import { dateColumnIntegratedFilterPredicate } from './plugins/filtering/DateColumnFilter';
import { useManageFilterState } from './plugins/filtering/helpers';
import { statusColumnFilterPredicate, textColumnIntegratedFilterPredicate } from './plugins/filtering/TextColumnFilter';
import { GetColumnFilterPredicate } from './plugins/filtering/types';
import { GridStateStoringPlugin } from './plugins/gridStateStoring.plugin';
import { IntegratedSelectionWrapper } from './plugins/integratedSelection.plugin';
import { gridTranslations } from './Translations/translations';

const useStyles = makeStyles(() => {
  // The SortLabel root and active selector is applied twice because an internal MUI selector is overwriting our styles by doing the same thing.
  const sortLabelRoot = '[class*="MuiTableSortLabel-root"]';
  const sortLabelActive = '[class*="MuiTableSortLabel-active"]';
  const svgIcon = '[class*="MuiSvgIcon-root"]';
  return {
    gridRoot: {
      '& [class*="MuiToolbar-root"]': {
        borderBottom: 'none',
        padding: '0px',
        '& .grouping-panel-empty-message': {
          ...theme.typography.h5,
          color: theme.palette.grey[900],
          padding: '0.625rem 0.75rem',
          borderWidth: '1px',
          borderStyle: 'dashed',
          borderColor: theme.palette.grey[600],
          borderRadius: '2px',
          width: '100%'
        },
        // Pagination Container -- Sibling selector because it gets built with no static class names
        '& + div + div': {
          height: theme.spacing(3),
          marginTop: theme.spacing(3),
          marginBottom: theme.spacing(2),
          overflow: 'visible',
          padding: 0,
          // Rows per page container -- descendant selector because it gets built with no static class names
          '& > div:nth-child(1)': {
            paddingRight: theme.spacing(4),
            // Page size message label
            '& [class*="ABO-PageSizeSelector-label"]': {
              color: theme.typography.body2.color,
              fontSize: theme.typography.body2.fontSize,
              paddingRight: theme.spacing(1)
            },
            // Page Size selector dropdown container
            '& .MuiInput-root': {
              backgroundColor: 'rgba(33, 33, 33, 0.06)',

              color: theme.typography.body2.color,
              fontSize: theme.typography.body2.fontSize,
              minWidth: theme.spacing(7),
              borderTopLeftRadius: theme.shape.borderRadius,
              borderTopRightRadius: theme.shape.borderRadius,
              '& >div': {
                borderBottom: '1px solid rgba(33,33,33,0.5)',
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0
              },
              '& >div:hover': {
                borderBottom: '1px solid rgba(33,33,33,1)'
              },
              '& >div[aria-expanded="true"]': {
                borderBottom: '3px solid #176DC2'
              },
              textAlign: 'left',
              // Page Size selector value display
              '& .MuiSelect-root': {
                height: theme.spacing(3)
              },
              '& .MuiInputBase-input': {
                paddingRight: 0,
                paddingTop: 0,
                paddingBottom: 0
              },
              // Page Size Selector Icon
              '& svg': {
                fontSize: '1.5rem',
                top: 'calc(50% - 12px)',
                right: 0,
                color: theme.palette.primary.main
              }
            }
          },
          // Pagination Controls Display Container -- descendant selector because it gets built with no static class names
          '& > div:nth-child(2)': {
            paddingRight: theme.spacing(4),
            // Pagination Rows Label
            '& > span': {
              color: theme.typography.body2.color,
              fontSize: theme.typography.body2.fontSize,
              paddingRight: theme.spacing(4)
            },
            // Pagination Buttons and page number indicators
            '& button': {
              padding: theme.spacing(0, 1),
              margin: 0,
              height: theme.spacing(3),
              minHeight: theme.spacing(3),
              maxHeight: theme.spacing(3)
            },
            '& > button[tabIndex="-1"]:not(:nth-child(2)):not(:last-child)': {
              backgroundColor: theme.palette.grey[100]
            },
            '& button:not(.Mui-disabled)': {
              color: theme.palette.primary.main
            }
          }
        }
      },
      '& [class*="MuiTableRow-root"]': {
        height: '36px'
      },
      '& [class*="MuiTableHead-root"]': {
        '& [class*="MuiTableRow-head"]': {
          '& [class*="MuiTableCell"]:first-child': {
            '& button[class*="IconButton"]': {
              opacity: 0,
              padding: 0,
              '&.infoPopperFilter': {
                opacity: 1,
                padding: theme.spacing(0.5)
              }
            }
          }
        },
        '& [class*="MuiTableCell-root"]': {
          borderBottom: `2px solid ${theme.palette.grey[800]}`,
          paddingTop: '0px',
          paddingBottom: '0px',
          paddingRight: theme.spacing(0.75),
          paddingLeft: theme.spacing(0.75),
          '&[class*=MuiTableCell-head]': {
            '&:first-child': {
              paddingLeft: theme.spacing(0.75)
            }
          },
          '& [class*="MuiTypography-root"]': {
            textTransform: 'uppercase'
          },
          '& .table-header-row-sort-label': {
            [`& ${sortLabelRoot}, ${sortLabelRoot}${sortLabelActive}${sortLabelRoot}${sortLabelActive}`]: {
              width: '100%',
              '& [class*="MuiTableSortLabel-icon"]': {
                fontSize: theme.spacing(2),
                color: theme.palette.grey[900]
              }
            }
          },
          '& .table-header-row-group-button': {
            display: 'flex',
            alignItems: 'center',
            [`& ${svgIcon}`]: {
              fontSize: theme.typography.fontSize,
              color: theme.palette.grey[600]
            }
          },
          '& [class*="ABO-ResizingControl-resizeHandleFirstLine"]:hover': {
            backgroundColor: 'unset'
          },
          '& [class*="ABO-ResizingControl-resizeHandleSecondLine"]:hover': {
            backgroundColor: 'unset'
          },
          '& [class*="resizeHandleLine"]': {
            backgroundColor: 'unset'
          },
          '& [class*="ResizingControl-resizeHandle"]': {
            right: '14px',
            width: '1px'
          },
          '&:hover': {
            '& .table-header-row-sort-label': {
              [`& ${sortLabelRoot}, ${sortLabelRoot}${sortLabelActive}${sortLabelRoot}${sortLabelActive}`]: {
                '& [class*="MuiTableSortLabel-icon"]': {
                  color: theme.palette.primary.main
                }
              }
            },
            '& .table-header-row-group-button': {
              [`& ${svgIcon}`]: {
                color: theme.palette.primary.main
              }
            },
            '& .table-header-row-group-button.table-header-row-group-button-disabled': {
              [`& ${svgIcon}`]: {
                color: theme.palette.grey[600]
              }
            }
          },
          '& .grid-table-header-row-cell-splitter': {
            borderRight: `1px solid ${theme.palette.grey[600]}`,
            paddingRight: '0.75rem',
            height: '1rem'
          },
          '&:last-child': {
            paddingRight: '0.375rem',
            '& .grid-table-header-row-cell-splitter': {
              display: 'none'
            }
          }
        }
      },
      '& [class*="ABO-MuiTableCell-body"]': {
        verticalAlign: 'top',
        '& [class*="ABO-TableTreeCellBase"]': {
          padding: '0 6px 6px 6px',
          verticalAlign: 'top',
          height: '26px'
        }
      },
      // TableContainer-root
      '& > div:nth-child(2)': {
        overflow: 'visible'
      },
      '& [class*="MuiInputBase-input"]': {
        // paddingLeft: theme.spacing(1),
        minHeight: '1.1875em',
        fontWeight: theme.typography.fontWeightMedium,
        lineHeight: theme.typography.body1.lineHeight,
        fontSize: theme.typography.body1.fontSize,
        color: theme.palette.text.primary
      },
      '& [class*="MuiInputBase-focused"]': {
        backgroundColor: 'rgba(0, 0, 0, 0.09)'
      },
      '& [class*="TableNoDataCell-textContainer"]': {
        // ie11 only - position: relative is overridden in Platform, so it is removed.
        '@media all and (-ms-high-contrast: none), (-ms-high-contrast: active)': {
          marginLeft: '50%'
        }
      },
      '& [class*="tdCellStyles"] [class*="MuiCheckbox-root"]': {
        padding: '7px 0'
      },

      '& [class*="MuiTableBody-root"]': {
        '& > tr >td': {
          borderBottom: `1px solid ${theme.palette.grey[600]}`
        },
        '& tr:nth-child(even):not(.TableSelectRow-selected)': {
          backgroundColor: '#F7F7F7'
        },
        '& [class*="MuiTableCell-root"]': {
          backgroundColor: 'inherit',
          padding: '7px 0 7px 5px',
          '& [class*="MuiIconButton-root"]': {
            margin: 0
          },
          '& [class*="MuiTableCell-cell"]': {
            padding: 0
          }
        },
        '& [class*="MuiTableCell-root"]:first-child': {
          paddingBottom: '0'
        },
        '& .table-group-row-cell div': {
          backgroundColor: 'inherit'
        }
      },
      '& [class*="TableSelectRow-selected"]': {
        backgroundColor: archerConstants.archerHover
      },
      '& td> div> button> span': {
        '& svg[class="ABO-MuiSvgIcon-root"]': {
          color: theme.palette.grey[900]
        }
      },
      '& [class*="MuiTypography-root"]': {
        textAlign: 'left'
      }
    },
    groupIconColors: {
      color: theme.palette.grey[900]
    }
  };
});

const Root: React.FunctionComponent<StyledRootProps> = props => {
  const { gridRoot } = useStyles();
  return <Grid.Root {...props} className={gridRoot} style={{ height: '100%', width: '100%' }} />;
};

// const label = { inputProps: { 'aria-label': 'SelectAll' } };

const ArcherGrid = (props: GridProps) => {
  const {
    columns,
    images,
    imageValue,
    actions,
    actionMessages,
    numberOptions,
    expandedGroups,
    filters,
    filtersDefault,
    grouping,
    groupingColumnExtensions,
    isAddNewEnabled,
    isEmailEnabled,
    isDeleteEnabled,
    isFilteringEnabled,
    isGroupingEnabled,
    isSortingEnabled,
    local,
    onAddNew,
    onEmailClick,
    onDeleteClick,
    onExpandedGroupsChange,
    onFiltersChange,
    showGroupingPanel,
    onGroupingChange,
    rows,
    defaultSorting, // Used in uncontrolled mode
    sorting, // Used in controlled mode
    onSortingChange,
    showAddNew,
    showColumnChooser,
    showEmail,
    showDelete,
    pageSizes,
    showPagination,
    totalCount,
    currentPage,
    changePageSize,
    changeCurrentPage,
    pageSize,
    translate,
    dateTimeLocale,
    dateTimeLocaleCode,
    columnSortingState,
    columnFilteringState,
    columnSortingFnOverrides,
    columnGroupingFnOverrides,
    columnVisibilityState,
    defaultColumnVisibilityState,
    columnWidths,
    defaultColumnWidths,
    onColumnWidthsChange,
    columnResizeEnabledNextColumn,
    enableRowSelection,
    enableTreeView,
    nestedTreeColumn,
    onRowSelection,
    enableVirtualTable,
    columnExtensions,
    imagePath,
    rowSelectionType,
    setExpandedRowIds,
    expandedRowIds,
    getChildRows,
    getRowId,
    toolbarMenuItems,
    storageKey
  } = props;

  const [selection, setSelection] = React.useState<number[]>([]);

  const changeSingleRowSelection = (currentSelections: number[]) => {
    let lastSelected: any = null;
    if (selection === [] || selection.length === 0) {
      [lastSelected] = currentSelections;
    } else {
      lastSelected = currentSelections.find((selected: number) => selection.indexOf(selected) === -1);
    }
    setSelection([lastSelected]);
    onRowSelection(rows[lastSelected]);
  };

  const changeMultipleRowSelection = (currentSelections: number[]) => {
    let enabledSelection: any = null;
    if (rows[0].disabledIndex !== undefined) {
      enabledSelection = currentSelections.filter(data => rows[data]?.disabledIndex);
    } else {
      enabledSelection = currentSelections;
    }
    setSelection(enabledSelection);
    onRowSelection(enabledSelection);
  };

  const deleteHandler = (event: any) => {
    if (onDeleteClick) {
      onDeleteClick(event);
      setSelection([]);
    }
  };
  const gridTranslationMessages = React.useMemo(() => gridTranslations(translate), [translate]);

  const { addClearFilterStateFn, removeClearFilterStateFn, handleClearFilters, notifyFiltersChanged } = useManageFilterState(onFiltersChange);

  const integratedFilteringColumnExtensions = columns.map(column => ({
    columnName: column.name,
    predicate: getColumnFilterPredicate(column)
  }));

  const actionColumnNames = columns.filter(column => column.type === 'actions');

  const columnFilteringStateWithoutActions = columnFilteringState?.concat(
    actionColumnNames.map(column => ({ columnName: column.name, filteringEnabled: false }))
  );
  const columnSortingStateWithoutActions = columnSortingState?.concat(
    actionColumnNames.map(column => ({ columnName: column.name, sortingEnabled: false }))
  );
  const groupIcon: any = ({ expanded }: TableGroupRow.IconProps) => {
    const { groupIconColors } = useStyles();
    return <IconButton icon={expanded ? 'ArrowDropDown' : 'ArrowRight'} className={groupIconColors} />;
  };
  return (
    <Grid rootComponent={Root} rows={rows} columns={columns} getRowId={getRowId}>
      <DateTypeProvider for={columns.filter(column => column.type === 'date').map(column => column.name)} dateTimeLocale={dateTimeLocale} />
      <CheckTypeProvider for={columns.filter(column => column.type === 'check').map(column => column.name)} />
      <NumberTypeProvider
        for={columns.filter(column => column.type === 'number').map(column => column.name)}
        availableFilterOperations={
          numberOptions
            ? numberOptions.availableFilterOperations
            : ['equal', 'notEqual', 'greaterThan', 'greaterThanOrEqual', 'lessThan', 'lessThanOrEqual']
        }
      />
      <ActionTypeProvider for={columns.filter(column => column.type === 'actions').map(column => column.name)} availableFilterOperations={[]} />
      <StatusTypeProvider
        for={columns.filter(column => column.type === 'status').map(column => column.name)}
        availableFilterOperations={['contains', 'startsWith', 'endsWith', 'equal', 'notEqual', 'empty', 'notEmpty']}
      />
      <DataTypeProvider
        for={columns.filter(column => column.type === undefined).map(column => column.name)}
        availableFilterOperations={['contains', 'startsWith', 'endsWith', 'equal', 'notEqual', 'empty', 'notEmpty']}
      />
      <FilteringState
        columnFilteringEnabled={isFilteringEnabled}
        filters={filters}
        defaultFilters={filtersDefault}
        onFiltersChange={notifyFiltersChanged}
        columnExtensions={columnFilteringStateWithoutActions}
      />
      <SortingState
        columnSortingEnabled={isSortingEnabled}
        sorting={sorting}
        defaultSorting={defaultSorting}
        onSortingChange={onSortingChange}
        columnExtensions={columnSortingStateWithoutActions}
      />
      <GroupingState
        columnGroupingEnabled={isGroupingEnabled}
        columnExtensions={groupingColumnExtensions}
        grouping={grouping}
        onGroupingChange={onGroupingChange}
        expandedGroups={expandedGroups}
        onExpandedGroupsChange={onExpandedGroupsChange}
      />
      {enableTreeView && <TreeDataState expandedRowIds={expandedRowIds} onExpandedRowIdsChange={setExpandedRowIds} />}
      {local && <IntegratedFiltering columnExtensions={integratedFilteringColumnExtensions} />}
      {local && <IntegratedSorting columnExtensions={columnSortingFnOverrides} />}

      {enableRowSelection && (
        <SelectionState
          selection={selection}
          onSelectionChange={rowSelectionType === 'single' ? (changeSingleRowSelection as any) : (changeMultipleRowSelection as any)}
        />
      )}
      {enableRowSelection && rowSelectionType === 'multiple' && <IntegratedSelectionWrapper />}
      <IntegratedGrouping columnExtensions={columnGroupingFnOverrides} />
      {showPagination && (
        <PagingState
          currentPage={currentPage}
          onCurrentPageChange={page => {
            setSelection([]);
            if (typeof changeCurrentPage === 'function') {
              changeCurrentPage(page);
            }
          }}
          onPageSizeChange={changePageSize}
          pageSize={pageSize}
        />
      )}
      {local && showPagination && <IntegratedPaging />}
      <DragDropProvider />
      {enableTreeView && getChildRows && <CustomTreeData getChildRows={getChildRows} />}
      {enableVirtualTable ? (
        <VirtualTable height="auto" tableComponent={TableComponent} cellComponent={TableCellComponent} columnExtensions={columnExtensions} />
      ) : (
        <Table
          messages={gridTranslationMessages}
          tableComponent={TableComponent}
          cellComponent={TableCellComponent}
          columnExtensions={columnExtensions}
        />
      )}
      {columnResizeEnabledNextColumn && (
        <TableColumnResizing
          columnWidths={columnWidths}
          defaultColumnWidths={defaultColumnWidths}
          onColumnWidthsChange={onColumnWidthsChange}
          resizingMode="nextColumn"
        />
      )}
      <TableHeaderRow
        showSortingControls={isSortingEnabled}
        cellComponent={TableHeaderRowCellComponent}
        titleComponent={TableHeaderRowTitleComponent}
        sortLabelComponent={TableHeaderRowSortLabel}
        groupButtonComponent={TableHeaderRowGroupButtonComponent}
        messages={gridTranslationMessages}
      />
      {enableRowSelection && (
        <TableSelection
          selectByRowClick={enableRowSelection}
          highlightRow={enableRowSelection}
          showSelectionColumn
          showSelectAll={rowSelectionType === 'multiple'}
          cellComponent={TableRowSelectCellComponent}
          // headerCellComponent={headerCellComponentProps => {
          //   return (
          //     <th>
          //       <MatCheckbox {...label} {...headerCellComponentProps} />
          //     </th>
          //   );
          // }}
        />
      )}
      {(showColumnChooser || columnVisibilityState || defaultColumnVisibilityState) && (
        <TableColumnVisibility
          messages={gridTranslations(translate)}
          hiddenColumnNames={columnVisibilityState}
          defaultHiddenColumnNames={defaultColumnVisibilityState}
        />
      )}
      {isFilteringEnabled && (
        <TableHeaderRowFilterPlugin
          messages={gridTranslationMessages}
          translate={translate}
          dateTimeLocaleCode={dateTimeLocaleCode}
          addClearFilterStateFn={addClearFilterStateFn}
          removeClearFilterStateFn={removeClearFilterStateFn}
          theme={theme}
        />
      )}
      {isGroupingEnabled && <TableGroupRow iconComponent={groupIcon} cellComponent={TableGroupRowCellComponent} />}
      <Toolbar />
      {isGroupingEnabled && showGroupingPanel && (
        <GroupingPanel
          messages={gridTranslationMessages}
          itemComponent={GroupingPanelItemComponent}
          showGroupingControls={isGroupingEnabled}
          showSortingControls={isSortingEnabled}
          emptyMessageComponent={GroupingPanelEmptyMessageComponent}
        />
      )}
      {isFilteringEnabled && <ToolbarClearFilters messages={gridTranslationMessages} handleClearFilters={handleClearFilters} />}
      {showColumnChooser && (
        <ColumnChooser
          messages={gridTranslationMessages}
          itemComponent={ColumnChooserItem}
          overlayComponent={ColumnChooserOverlay}
          toggleButtonComponent={ToggleButtonComponent}
        />
      )}
      {enableTreeView && <TableTreeColumn for={nestedTreeColumn} expandButtonComponent={treeExpandIcon(gridTranslationMessages)} />}
      {showEmail && <ToolbarEmail translate={translate} onClick={onEmailClick} disabled={!isEmailEnabled} />}
      {showAddNew && <ToolbarAddNew translate={translate} onClick={onAddNew} disabled={!isAddNewEnabled} />}
      {showDelete && <ToolbarDelete translate={translate} onClick={deleteHandler} disabled={!isDeleteEnabled} />}
      {toolbarMenuItems && toolbarMenuItems.length > 0 && <ToolbarMore translate={translate} menuItems={toolbarMenuItems} />}
      {images && <ImagePlugin translate={translate} imageValue={imageValue} imagePath={imagePath} />}
      {actions && <ActionsPlugin translate={translate} actions={actions} messages={actionMessages} />}
      {showPagination && <CustomPaging totalCount={totalCount} />}
      {showPagination && <PagingPanel messages={gridTranslationMessages} pageSizes={pageSizes} containerComponent={PagingPanelContainer} />}
      {storageKey && <GridStateStoringPlugin storageKey={storageKey} />}
    </Grid>
  );
};

ArcherGrid.defaultProps = {
  showPagination: false,
  translate: (value: any) => value
};

ArcherGrid.displayName = 'ArcherGrid';

/**
 * Utilities
 */
const getColumnFilterPredicate: GetColumnFilterPredicate = column => {
  switch (column.type) {
    case 'check':
    case 'number':
      return IntegratedFiltering.defaultPredicate;
    case 'date':
      return dateColumnIntegratedFilterPredicate;
    case 'status':
      return statusColumnFilterPredicate;
    default:
      return textColumnIntegratedFilterPredicate;
  }
};

export { ArcherGrid };
